import FetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/Queries/FetchBeneficiary';
import fetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/fetchBeneficiary';
import { PermissionEnum } from '@Application/Security/Roles';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import StaffAssignmentPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryMembers/StaffAssignmentPanel';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Props = {
	readonly beneficiaryId: number;
};

/**
 * Loads the beneficiary and mounts the staff panel under `SUBJECT_WRITE` + `STAFF_READ`. The panel
 * stays out of an archived dossier, which is read-only. A failed or pending read renders nothing: the
 * members above carry the screen's own loading and error states.
 */
const BeneficiaryStaffAssignment: React.FC<Props> = ({ beneficiaryId }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const { data: beneficiary } = useStoreQuery({
		selector: (state) => state.beneficiariesStore.beneficiary,
		stopOn: (value) => value !== null && value.id === beneficiaryId,
		query: () => dispatch(fetchBeneficiary(new FetchBeneficiary(facilityId, beneficiaryId))),
		deps: [beneficiaryId, facilityId],
	});

	const editableBeneficiary =
		beneficiary !== null && beneficiary.id === beneficiaryId && !beneficiary.isArchived
			? beneficiary
			: null;

	return (
		<WithPermission permission={[PermissionEnum.SUBJECT_WRITE, PermissionEnum.STAFF_READ]} mode="all">
			<ConditionalLoader data={editableBeneficiary}>
				{(present) => <StaffAssignmentPanel beneficiary={present} facilityId={facilityId} />}
			</ConditionalLoader>
		</WithPermission>
	);
};

export default BeneficiaryStaffAssignment;
